import { ESRThunkDispatch, listAllUsers } from "@/actions/form";
import { ESRState } from "@/types/data";
import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

type UserOption = {
	email: string;
	name: string;
};

interface GrantEditPermissionDialogProps {
	open: boolean;
	title: string;
	details?: string;
	onClose: () => void;
	// Only the selected user's email is handed back to the caller
	onGrant: (email: string) => void;
}

export const GrantEditPermissionDialog: FC<GrantEditPermissionDialogProps> = ({
	open,
	title,
	details,
	onClose,
	onGrant,
}) => {
	const dispatch = useDispatch<ESRThunkDispatch>();
	const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
	const user_list: UserOption[] = useSelector((state: ESRState) =>
		Object.keys(state.user_list).map((email: string) => {
			const user = state.user_list[email];
			return {
				email: email,
				name: user.first_name + " " + user.last_name,
			};
		}),
	);
	// fetch registered users only when the dialog is opened
	useEffect(() => {
		if (!open) return;
		setSelectedUser(null);
		dispatch(listAllUsers());
	}, [open]);

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					{details && (
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							{details}
						</Typography>
					)}
					<Autocomplete
						options={user_list}
						value={selectedUser}
						onChange={(_, value) => setSelectedUser(value)}
						getOptionLabel={(option) => `${option.name} (${option.email})`}
						isOptionEqualToValue={(option, value) =>
							option.email === value.email
						}
						filterOptions={(options, { inputValue }) =>
							options.filter(
								(option) =>
									option.name
										.toLowerCase()
										.includes(inputValue.toLowerCase()) ||
									option.email.toLowerCase().includes(inputValue.toLowerCase()),
							)
						}
						renderInput={(params) => (
							<TextField
								{...params}
								margin="dense"
								label="User"
								placeholder="Search by name or email"
							/>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						variant="contained"
						disabled={!selectedUser}
						onClick={() => {
							if (selectedUser) onGrant(selectedUser.email);
							onClose();
						}}
					>
						Grant
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
